import React, { useEffect } from "react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Stack,
} from "@mui/material"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { LoadingButton } from "@mui/lab"
import {
  addCategory,
  updateCategory,
  fetchCategoryList,
  selectCategories,
  CategoriesState,
  setCategoryFormClose,
} from "../../slices/categorySlice"
import { constructGetCategoryListApiRequestParams } from "../../utils/categoryHelper"
import CommonAutocomplete from "../../components/CommonAutocomplete/CommonAutocomplete"
import { Category } from "../../models/category.model"
import { useAppDispatch, useAppSelector } from "../../app/hooks"

interface CategoryFormProps {
  open: any
  category: keyof CategoriesState
  level: 1 | 2 | 3
  parentCategoryKey?: keyof CategoriesState
  parentCategoryLabel?: string
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  category,
  level,
  parentCategoryKey = "Category1",
  parentCategoryLabel = "Category",
}) => {
  const dispatch = useAppDispatch()
  const isEdit = !!(open?.level3Id ?? open?.level2Id ?? open?.level1Id)

  const showSourceField = level === 1
  const showParentCategoryAutocomplete = level === 2
  const showParentSubCategoryAutocomplete = level === 3

  const { categoryList, isCategoryLoading } = useAppSelector(
    (state) =>
      selectCategories(state)[parentCategoryKey] || { categoryList: [] },
  )

  const fetchCategory = (value?: string) => {
    dispatch(
      fetchCategoryList(
        constructGetCategoryListApiRequestParams(parentCategoryKey, {
          search: value || "",
        }),
      ),
    )
  }

  useEffect(() => {
    if (showParentCategoryAutocomplete || showParentSubCategoryAutocomplete) {
      fetchCategory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const initialValues = {
    id: open?.level3Id ?? open?.level2Id ?? open?.level1Id ?? "",
    // id: open?.id ?? "",
    label: open?.level3Name ?? open?.level2Name ?? open?.level1Name ?? "",
    source: open?.source ?? "",
    category: category,
    level,
    parentId: showParentSubCategoryAutocomplete
      ? open?.level2Id
      : open?.level1Id ?? "",
  }

  const validationSchema = Yup.object({
    label: Yup.string().required(
      `${level === 1 ? "Category" : "Sub-category"} Name is required`,
    ),
    ...(showSourceField && {
      source: Yup.string().required("Source is required"),
    }),
    ...(showParentCategoryAutocomplete && {
      parentId: Yup.string().required(`${parentCategoryLabel} is required`),
    }),
    ...(showParentSubCategoryAutocomplete && {
      parentId: Yup.string().required(`${parentCategoryLabel} is required`),
    }),
  })

  const handleDialogClose = () => {
    dispatch(setCategoryFormClose({ category }))
  }

  const handleSubmit = async (values: any) => {
    const payload = {
      id: values.id,
      label: values.label,
      level: values.level,
      category: values.category,
      ...(showSourceField && { source: values.source }),
      ...(showParentCategoryAutocomplete && { category1: values.parentId }),
      ...(showParentSubCategoryAutocomplete && {
        category2: values.parentId,
      }),
      ...(showParentSubCategoryAutocomplete &&
        initialValues.parentId !== values.parentId && {
          initialCategory2: initialValues.parentId,
        }),
    }

    try {
      if (isEdit) {
        dispatch(updateCategory(payload))
      } else {
        dispatch(addCategory(payload))
      }
    } catch (error) {
      console.error("Error submitting category:", error)
    }
  }

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogTitle>
        {isEdit ? "Edit" : "Add"} {level === 1 ? "Category" : "Sub-category"}
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, handleBlur }) => (
            <Form>
              <Stack gap={2} sx={{ minWidth: "100%", marginBlockStart: "5px" }}>
                {showSourceField && (
                  <FormControl fullWidth size="small">
                    <InputLabel id="source-label">Source</InputLabel>
                    <Select
                      labelId="source-label"
                      id="source-select"
                      label="Source"
                      name="source"
                      value={values.source}
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue("source", e.target.value)}
                      error={!!errors.source}
                    >
                      <MenuItem value="alts">Alts</MenuItem>
                      <MenuItem value="workspace">Workspace</MenuItem>
                    </Select>
                    <FormHelperText sx={{ color: "red" }}>
                      {errors.source as string}
                    </FormHelperText>
                  </FormControl>
                )}

                {showParentCategoryAutocomplete && (
                  <CommonAutocomplete
                    options={categoryList}
                    getOptionLabel={(option: Category) =>
                      option?.level1Name ?? ""
                    }
                    value={
                      categoryList.find(
                        (item) => item.level1Id === values.parentId,
                      ) || null
                    }
                    onChange={(val: any) => {
                      setFieldValue("parentId", val?.level1Id || "")
                    }}
                    placeholder="Type a name to see options"
                    label={parentCategoryLabel}
                    fetchOptionsDebounced={fetchCategory}
                  />
                )}
                {showParentSubCategoryAutocomplete && (
                  <CommonAutocomplete
                    options={categoryList}
                    getOptionLabel={(option: Category) =>
                      option?.level2Name ?? ""
                    }
                    value={
                      categoryList.find(
                        (item) => item.level2Id === values.parentId,
                      ) || null
                    }
                    onChange={(val: any) => {
                      setFieldValue("parentId", val?.level2Id || "")
                    }}
                    placeholder="Type a name to see options"
                    label={parentCategoryLabel}
                    fetchOptionsDebounced={fetchCategory}
                  />
                )}

                <TextField
                  size="small"
                  label={level === 1 ? "Category Name" : "Sub-category Name"}
                  name="label"
                  value={values.label}
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue("label", e.target.value)}
                  error={!!errors.label}
                  helperText={errors.label as string}
                  fullWidth
                />

                <Stack direction="row" gap={2}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDialogClose}
                    fullWidth
                  >
                    Back
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    loading={isCategoryLoading}
                    disabled={
                      JSON.stringify(values) ===
                        JSON.stringify(initialValues) ||
                      ((!values.parentId || !values.source) && !values.label)
                    }
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default CategoryForm
